import React, { useEffect, useMemo, useState } from 'react';
import AuthentificationApi from '../../api/AuthentificationApi';
import '../../fichierCss/FormulaireReinitialiserMotDePasse.css';

interface FormulaireReinitialiserMotDePasseProps {
  ouvert: boolean;
  fermeture: () => void;
  token: string;
}

const FormulaireReinitialiserMotDePasse = ({ ouvert, fermeture, token }: FormulaireReinitialiserMotDePasseProps) => {
  const [motDePasse, setMotDePasse] = useState('');
  const [confirmationMotDePasse, setConfirmationMotDePasse] = useState('');
  const [afficherMotDePasse, setAfficherMotDePasse] = useState(false);
  const [afficherConfirmationMotDePasse, setAfficherConfirmationMotDePasse] = useState(false);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');

  const motsDePasseConcordent = useMemo(
    () => confirmationMotDePasse.length > 0 && motDePasse === confirmationMotDePasse,
    [motDePasse, confirmationMotDePasse]
  );

  useEffect(() => {
    if (!ouvert) {
      return undefined;
    }

    const gererTouche = (event) => {
      if (event.key === 'Escape') {
        fermeture();
      }
    };

    window.addEventListener('keydown', gererTouche);

    return () => {
      window.removeEventListener('keydown', gererTouche);
    };
  }, [ouvert, fermeture]);

  if (!ouvert) {
    return null;
  }

  const soumettreReinitialisation = async (event) => {
    event.preventDefault();
    setErreur('');
    setSucces('');

    if (motDePasse !== confirmationMotDePasse) {
      setErreur('Les mots de passe ne correspondent pas.');
      return;
    }

    if (!token) {
      setErreur('Token invalide ou expire.');
      return;
    }

    setChargement(true);

    try {
      await AuthentificationApi.reinitialiserMotDePasse({
        token,
        nouveauMotDePasse: motDePasse
      });

      setSucces('Mot de passe reinitialise avec succes. Vous pouvez maintenant vous connecter.');

      setTimeout(() => {
        fermeture();
      }, 2000);
    } catch (exception) {
      setErreur('Impossible de reinitialiser le mot de passe. Le lien a peut-etre expire.');
    } finally {
      setChargement(false);
    }
  };

  const afficherAideConcordance = confirmationMotDePasse.length > 0 && !motsDePasseConcordent;

  return (
    <div
      className="reinit-mdp-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="titre-reinit-mdp"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          fermeture();
        }
      }}
    >
      <div className="reinit-mdp-modal-card">
        <button
          type="button"
          className="reinit-mdp-close"
          onClick={fermeture}
          aria-label="Fermer"
        >
          ×
        </button>

        <div className="reinit-mdp-badge">Securite</div>
        <h2 id="titre-reinit-mdp">Creer un nouveau mot de passe</h2>
        <p className="reinit-mdp-subtitle">Choisissez un mot de passe securise pour votre compte.</p>

        <form className="reinit-mdp-form" onSubmit={soumettreReinitialisation}>
          <label htmlFor="mot-de-passe-reinit">Nouveau mot de passe</label>
          <div className="champ-mot-de-passe">
            <input
              id="mot-de-passe-reinit"
              type={afficherMotDePasse ? 'text' : 'password'}
              value={motDePasse}
              onChange={(event) => setMotDePasse(event.target.value)}
              placeholder="Choisissez un mot de passe"
              minLength={8}
              required
            />
            <button
              type="button"
              className="toggle-mdp-reinit"
              onClick={() => setAfficherMotDePasse((precedent) => !precedent)}
              aria-label={afficherMotDePasse ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              {afficherMotDePasse ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              )}
            </button>
          </div>

          <label htmlFor="mot-de-passe-confirmation-reinit">Confirmer le mot de passe</label>
          <div className="champ-mot-de-passe">
            <input
              id="mot-de-passe-confirmation-reinit"
              type={afficherConfirmationMotDePasse ? 'text' : 'password'}
              value={confirmationMotDePasse}
              onChange={(event) => setConfirmationMotDePasse(event.target.value)}
              placeholder="Retapez votre mot de passe"
              minLength={8}
              required
            />
            <button
              type="button"
              className="toggle-mdp-reinit"
              onClick={() => setAfficherConfirmationMotDePasse((precedent) => !precedent)}
              aria-label={afficherConfirmationMotDePasse ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              {afficherConfirmationMotDePasse ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              )}
            </button>
          </div>

          {afficherAideConcordance && (
            <div className="reinit-mdp-feedback erreur">Les mots de passe ne correspondent pas.</div>
          )}
          {erreur && <div className="reinit-mdp-feedback erreur">{erreur}</div>}
          {succes && <div className="reinit-mdp-feedback succes">{succes}</div>}

          <button
            type="submit"
            className="reinit-mdp-submit"
            disabled={chargement || !motsDePasseConcordent}
          >
            {chargement ? 'Reinitialisation...' : 'Reinitialiser le mot de passe'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FormulaireReinitialiserMotDePasse;
